/**
 * useCountrySelector Hook
 * Custom hook for country selector utilities
 * Provides translated country options and helper functions
 */

import { ALL_COUNTRIES } from '@/constants/countries';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { CountryOption } from '@/types/country.types';
import { useCallback } from 'react';

/**
 * Custom hook for country selector utilities
 * Simplifies integration with React Hook Form and provides additional utilities
 *
 * @returns Object containing country utilities
 */
export function useCountrySelector() {
  const { t: tForms } = useFormsTranslation();

  /**
   * Get all country options with translated labels
   * @returns Array of country options with value (ISO code) and label (translated name)
   */
  const getCountryOptions = useCallback((): CountryOption[] => {
    return ALL_COUNTRIES.map(country => ({
      value: country.code,
      label: tForms(country.translationKey),
    }));
  }, [tForms]);

  /**
   * Get translated country name by ISO code
   * @param code - ISO 3166-1 alpha-2 country code (e.g., "CH", "DE", "AT")
   * @returns Translated country name or the code itself if not found
   */
  const getCountryName = useCallback(
    (code: string): string => {
      const country = ALL_COUNTRIES.find(c => c.code === code);
      return country ? tForms(country.translationKey) : code;
    },
    [tForms]
  );

  /**
   * Check if a country is a priority country
   * Priority countries: Switzerland (CH), Germany (DE), Austria (AT)
   * @param code - ISO 3166-1 alpha-2 country code
   * @returns True if the country is a priority country, false otherwise
   */
  const isPriorityCountry = useCallback((code: string): boolean => {
    return ['CH', 'DE', 'AT'].includes(code);
  }, []);

  /**
   * Get country object by ISO code
   * @param code - ISO 3166-1 alpha-2 country code
   * @returns Country object or undefined if not found
   */
  const getCountryByCode = useCallback((code: string) => {
    return ALL_COUNTRIES.find(c => c.code === code);
  }, []);

  return {
    countries: ALL_COUNTRIES,
    getCountryOptions,
    getCountryName,
    isPriorityCountry,
    getCountryByCode,
  };
}
